import {Megaphone, ShieldAlert} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ColonyAvatar from '@/components/ColonyAvatar'
import ColonyOfficialStar from '@/components/ColonyOfficialStar'
import NewsFeedComposer from '@/components/NewsFeedComposer'
import PostThread from '@/components/PostThread'
import SafePostText from '@/components/SafePostText'
import { colonyProfilePath } from '@/lib/colonyProfile'
import { formatRelativeTime } from '@/lib/format'
import { listProjectPosts, onProjectPostCreated, onProjectPostDeleted, type ProjectPost, type ProjectPostKind } from '@/lib/projectPosts'
import { colonyDisplayName, listWalletProfiles, onWalletProfileUpdated, type WalletProfileMap } from '@/lib/walletProfiles'

export default function NewsFeedInner({
  projectKey,
  kind,
  tokenAddress,
  accent = 'lime',
}: {
  projectKey: string
  kind: ProjectPostKind
  tokenAddress: string
  accent?: 'lime' | 'orange'
}) {
  const [posts, setPosts] = useState<ProjectPost[]>([])
  const [avatars, setAvatars] = useState<WalletProfileMap>({})
  const [loading, setLoading] = useState(true)
  const normalizedKey = projectKey.trim().toLowerCase()

  useEffect(() => {
    let active = true
    setLoading(true)
    listProjectPosts(projectKey).then((list) => {
      if (active)
        setPosts(list)
    }).finally(() => {
      if (active)
        setLoading(false)
    })
    return () => { active = false }
  }, [projectKey])

  const posterWallets = posts.map(post => post.wallet).filter(Boolean).join(',')

  useEffect(() => {
    if (!posterWallets)
      return
    const wallets = posterWallets.split(',')
    let active = true
    listWalletProfiles(wallets).then((map) => {
      if (active)
        setAvatars(prev => ({ ...prev, ...map }))
    })
    return () => { active = false }
  }, [posterWallets])

  useEffect(() => {
    return onProjectPostCreated(({ projectKey: key, post }) => {
      if (key !== normalizedKey)
        return
      setPosts(prev => [post, ...prev.filter(item => item.id !== post.id)])
    })
  }, [normalizedKey])

  useEffect(() => {
    return onProjectPostDeleted((postId) => {
      setPosts(prev => prev.filter(item => item.id !== postId))
    })
  }, [])

  useEffect(() => {
    return onWalletProfileUpdated((profile) => {
      setAvatars(prev => ({
        ...prev,
        [profile.wallet.toLowerCase()]: {
          image: profile.image || prev[profile.wallet.toLowerCase()]?.image || '',
          username: profile.username ?? prev[profile.wallet.toLowerCase()]?.username ?? '',
        },
      }))
    })
  }, [])

  const accentIcon = accent === 'orange' ? 'text-orange-400' : 'text-lime-400'

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md">
      <div className="flex items-center gap-2">
        <Megaphone className={`h-4 w-4 ${accentIcon}`} aria-hidden="true" />
        <h2 className="text-sm font-semibold text-white">News Feed</h2>
      </div>
      <p className="mt-1 text-xs text-zinc-500">Anonymous holder posts for this project. Anyone can read; only wallets holding the token can make a post.</p>
      <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-200/80">
        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" aria-hidden="true" />
        Do not trust any links from the News Feed. Stay safe — Penguin Pad never makes those links clickable.
      </p>

      <div className="mt-4">
        <NewsFeedComposer projectKey={projectKey} kind={kind} tokenAddress={tokenAddress} accent={accent} />
      </div>

      {loading
        ? <p className="mt-4 text-sm text-zinc-500">Loading posts…</p>
        : posts.length > 0
          ? (
              <ul className="mt-4 divide-y divide-white/5">
                {posts.map(post => (
                  <li key={post.id} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <Link
                        to={colonyProfilePath(post.wallet)}
                        aria-label="Open Colony profile"
                        className="shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400/60"
                      >
                        <ColonyAvatar wallet={post.wallet} image={avatars[post.wallet?.toLowerCase()]?.image} size="sm" anonymous />
                      </Link>
                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-center gap-x-1.5 text-[11px] text-zinc-500">
                          <Link
                            to={colonyProfilePath(post.wallet)}
                            className="inline-flex items-center gap-1 font-medium text-white hover:text-lime-300"
                          >
                            {colonyDisplayName(post.wallet, avatars[post.wallet?.toLowerCase()]?.username)}
                            <ColonyOfficialStar wallet={post.wallet} size="sm" />
                          </Link>
                          <span aria-hidden="true">·</span>
                          {formatRelativeTime(post.createdAt)}
                        </p>
                        <SafePostText text={post.content} className="mt-1" />
                        <PostThread postKind="project" postId={post.id} image={post.image} accent={accent} imageAlt="News Feed photo" />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )
          : <p className="mt-4 text-sm text-zinc-500">No posts yet. Only wallets holding this token can make a post.</p>}
    </div>
  )
}
